import { useContext, useMemo, useRef } from "react";
import { hashMetricDependency, MetricDependency } from "overlay-engine";
import {
  CompatibleSpatialMetricDetailsFragment,
  Geography,
  OverlaySourceDetailsFragment,
  ReportContextSketchClassDetailsFragment,
  SpatialMetricState,
} from "../../generated/graphql";
import { DraftReportContext } from "../DraftReportContext";
import { filterMetricsByDependencies } from "../utils/metricSatisfiesDependency";
import { useCardDependenciesContext } from "../context/CardDependenciesContext";
import { useSubjectReportContext } from "../context/SubjectReportContext";

type SketchClassForWidgets = Pick<
  ReportContextSketchClassDetailsFragment,
  | "id"
  | "projectId"
  | "geometryType"
  | "form"
  | "clippingGeographies"
  | "project"
  | "validChildren"
>;

export type WidgetDependenciesResult = {
  metrics: CompatibleSpatialMetricDetailsFragment[];
  sources: OverlaySourceDetailsFragment[];
  loading: boolean;
  errors: string[];
  geographies: Pick<
    Geography,
    "id" | "name" | "translatedProps" | "stableIds"
  >[];
  sketchClass: SketchClassForWidgets | null;
};

/**
 * Stable comparison for metrics array - only returns new array if content changes
 */
function useStableMetrics(
  metrics: CompatibleSpatialMetricDetailsFragment[]
): CompatibleSpatialMetricDetailsFragment[] {
  const ref = useRef<CompatibleSpatialMetricDetailsFragment[]>(metrics);
  const prevKey = useRef<string>("");

  // Create a key based on metric id, state, and value hash
  const key = metrics
    .map(
      (m) =>
        `${m.id}:${m.state}:${m.dependencyHash}:${JSON.stringify(
          m.value
        )?.slice(0, 100)}`
    )
    .sort()
    .join("|");

  if (key !== prevKey.current) {
    prevKey.current = key;
    ref.current = metrics;
  }

  return ref.current;
}

/**
 * Stable comparison for sources array - only returns new array if content changes
 */
function useStableSources(
  sources: OverlaySourceDetailsFragment[]
): OverlaySourceDetailsFragment[] {
  const ref = useRef<OverlaySourceDetailsFragment[]>(sources);
  const prevKey = useRef<string>("");

  const key = sources
    .map((s) => `${s.tableOfContentsItemId}:${s.sourceUrl}`)
    .sort()
    .join("|");

  if (key !== prevKey.current) {
    prevKey.current = key;
    ref.current = sources;
  }

  return ref.current;
}

function sourceProcessingInProgress(
  source: OverlaySourceDetailsFragment
): boolean {
  const jobState = source.sourceProcessingJob?.state;
  if (jobState === SpatialMetricState.Complete) return false;
  if (jobState === SpatialMetricState.Error) return false;
  if (
    jobState === SpatialMetricState.Queued ||
    jobState === SpatialMetricState.Processing
  ) {
    return true;
  }
  return !source.output?.url;
}

/**
 * Hook that returns stable widget dependencies, only triggering re-renders
 * when the widget's specific metrics/sources actually change.
 *
 * This hook consumes CardDependenciesContext to get card-scoped metrics and
 * sources, then filters further based on the widget's specific dependencies.
 * This prevents re-renders when other cards' data changes.
 */
export function useWidgetDependencies(
  dependencies: MetricDependency[] | undefined
): WidgetDependenciesResult {
  // Get card-level dependencies from context (provided by ReportCard)
  const {
    metrics: cardMetrics,
    sources: cardSources,
    geographies: contextGeographies,
    sketchClass: contextSketchClass,
    globalErrors,
    dependenciesAwaitingRefresh,
    dependencyResolutionFailuresByHash,
  } = useCardDependenciesContext();

  // Also get draft metrics/sources for editor scenarios
  const draftReportContext = useContext(DraftReportContext);
  const subjectReportContext = useSubjectReportContext();

  /** Fragment-backed metrics are never produced when a collection has no sketches / fragments. */
  const fragmentMetricsWillNeverExist =
    !subjectReportContext.loading &&
    subjectReportContext.data?.isCollection === true &&
    (subjectReportContext.data.relatedFragments?.length ?? 0) === 0;

  const resolutionFailuresByHash = useMemo(
    () => ({
      ...dependencyResolutionFailuresByHash,
      ...draftReportContext.draftDependencyFailuresByHash,
    }),
    [
      dependencyResolutionFailuresByHash,
      draftReportContext.draftDependencyFailuresByHash,
    ],
  );

  // Combine card sources with draft sources
  const allSources = useMemo(() => {
    const combined = [
      ...cardSources,
      ...draftReportContext.draftOverlaySources,
    ];
    // Dedupe by stableId
    const sourceIds = new Set<string>();
    return combined.filter((s) => {
      if (s.stableId && sourceIds.has(s.stableId)) {
        return false;
      }
      if (s.stableId) {
        sourceIds.add(s.stableId);
      }
      return true;
    });
  }, [cardSources, draftReportContext.draftOverlaySources]);

  // Compute source URL map for filtering metrics
  const sourceUrlMap = useMemo(() => {
    return allSources.reduce((acc, s) => {
      if (s.stableId && s.sourceUrl) {
        acc[s.stableId] = s.sourceUrl;
      }
      return acc;
    }, {} as Record<string, string>);
  }, [allSources]);

  // Combine card metrics with draft metrics
  const allMetrics = useMemo(() => {
    const seenIds = new Set<number>();
    const all = [] as CompatibleSpatialMetricDetailsFragment[];
    for (const metric of [...cardMetrics, ...draftReportContext.draftMetrics]) {
      if (metric.id && seenIds.has(metric.id)) continue;
      if (metric.id) seenIds.add(metric.id);
      all.push(metric);
    }
    return all;
  }, [cardMetrics, draftReportContext.draftMetrics]);

  // Filter metrics and sources for this widget
  const { rawMetrics, rawSources, loading, errors } = useMemo(() => {
    let loading = dependenciesAwaitingRefresh;
    const errors: string[] = [];

    const filteredMetrics = filterMetricsByDependencies(
      allMetrics,
      dependencies || [],
      sourceUrlMap
    ) as CompatibleSpatialMetricDetailsFragment[];

    for (const metric of filteredMetrics) {
      if (
        metric.state === SpatialMetricState.DependencyNotReady ||
        metric.state === SpatialMetricState.Processing ||
        metric.state === SpatialMetricState.Queued
      ) {
        loading = true;
      }
      if (metric.state === SpatialMetricState.Error) {
        errors.push(metric.errorMessage || "Unknown error");
      }
    }

    const filteredSources = allSources.filter((s) =>
      (dependencies || []).some(
        (d: MetricDependency) => d.stableId === s.stableId
      )
    );

    for (const source of filteredSources) {
      if (sourceProcessingInProgress(source)) {
        loading = true;
      }
      if (source.sourceProcessingJob?.state === SpatialMetricState.Error) {
        errors.push(
          source.sourceProcessingJob?.errorMessage || "Unknown error"
        );
      }
    }

    for (const dependency of dependencies || []) {
      if (
        fragmentMetricsWillNeverExist &&
        dependency.subjectType === "fragments"
      ) {
        continue;
      }
      const hash = hashMetricDependency(dependency, sourceUrlMap);
      const relatedMetric = filteredMetrics.find(
        (m) => m.dependencyHash === hash
      );
      if (!relatedMetric) {
        const resolutionErr = resolutionFailuresByHash[hash];
        if (resolutionErr) {
          errors.push(resolutionErr);
        } else {
          loading = true;
        }
      }

      if (dependency.stableId) {
        const sourceKnown = allSources.some(
          (s) => s.stableId === dependency.stableId
        );
        if (!sourceKnown) {
          const resolutionErr = resolutionFailuresByHash[hash];
          if (resolutionErr) {
            if (!errors.includes(resolutionErr)) {
              errors.push(resolutionErr);
            }
          } else {
            loading = true;
          }
        }
      }
    }

    if (globalErrors.length > 0) {
      for (const msg of globalErrors) {
        if (!errors.includes(msg)) {
          errors.push(msg);
        }
      }
    }

    return {
      rawMetrics: filteredMetrics,
      rawSources: filteredSources,
      loading,
      errors,
    };
  }, [
    allMetrics,
    allSources,
    dependencies,
    sourceUrlMap,
    globalErrors,
    dependenciesAwaitingRefresh,
    fragmentMetricsWillNeverExist,
    resolutionFailuresByHash,
  ]);

  // Use stable references - only change when content actually changes
  const metrics = useStableMetrics(rawMetrics);
  const sources = useStableSources(rawSources);

  // Stabilize the loading and errors values
  const stableLoading = useStableValue(loading);
  const stableErrors = useStableArray(errors);

  // Stabilize geographies and sketchClass
  const geographies = useStableGeographies(contextGeographies);
  const sketchClass = useStableSketchClass(contextSketchClass);

  return {
    metrics,
    sources,
    loading: stableLoading,
    errors: stableErrors,
    geographies,
    sketchClass,
  };
}

/**
 * Returns stable reference for a primitive value
 */
function useStableValue<T>(value: T): T {
  const ref = useRef<T>(value);
  if (ref.current !== value) {
    ref.current = value;
  }
  return ref.current;
}

/**
 * Returns stable reference for a string array
 */
function useStableArray(arr: string[]): string[] {
  const ref = useRef<string[]>(arr);
  const prevKey = useRef<string>("");

  const key = [...arr].sort().join("|");
  if (key !== prevKey.current) {
    prevKey.current = key;
    ref.current = arr;
  }

  return ref.current;
}

/**
 * Returns stable reference for geographies array
 */
function useStableGeographies(
  geographies: Pick<
    Geography,
    "id" | "name" | "translatedProps" | "stableIds"
  >[]
): Pick<Geography, "id" | "name" | "translatedProps" | "stableIds">[] {
  const ref = useRef(geographies);
  const prevKey = useRef<string>("");

  const key = geographies.map((g) => `${g.id}:${g.name}`).join("|");
  if (key !== prevKey.current) {
    prevKey.current = key;
    ref.current = geographies;
  }

  return ref.current;
}

/**
 * Returns stable reference for sketchClass
 */
function useStableSketchClass(
  sketchClass: SketchClassForWidgets | null
): SketchClassForWidgets | null {
  const ref = useRef<SketchClassForWidgets | null>(sketchClass);
  const prevKey = useRef<string>("");

  const key = sketchClass
    ? `${sketchClass.id}:${sketchClass.geometryType}`
    : "null";
  if (key !== prevKey.current) {
    prevKey.current = key;
    ref.current = sketchClass;
  }

  return ref.current;
}
